import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileSearch, Filter, Search } from 'lucide-react';
import {
  BosDurum,
  Girdi,
  TarihAraligiSecici,
  VeriTablo,
  tarihIso,
  type ColumnDef,
  type TarihAraligi,
} from '@emanetx/ui';
import { denetimKayitlariniListele, type DenetimKaydi } from '@emanetx/sdk';
import { istemci } from '../istemci.js';

function son7Gun(): TarihAraligi {
  const e = new Date();
  const s = new Date();
  s.setDate(s.getDate() - 6);
  s.setHours(0, 0, 0, 0);
  return { baslangic: s, bitis: e };
}

export function SayfaDenetim() {
  const [sayfa, setSayfa] = useState(1);
  const [aralik, setAralik] = useState<TarihAraligi>(son7Gun);
  const [eylem, setEylem] = useState('');
  const [kaynak, setKaynak] = useState('');

  const sorgu = {
    sayfa,
    boyut: 100,
    ...(eylem && { eylem }),
    ...(kaynak && { kaynak }),
    ...(tarihIso(aralik.baslangic) && { baslangicTen: tarihIso(aralik.baslangic) }),
    ...(tarihIso(aralik.bitis) && { baslangicAKadar: tarihIso(aralik.bitis) }),
  };

  const liste = useQuery({
    queryKey: ['denetim', sorgu],
    queryFn: () => denetimKayitlariniListele(istemci, sorgu),
    placeholderData: (o) => o,
  });

  const kolonlar = useMemo<ColumnDef<DenetimKaydi, unknown>[]>(
    () => [
      {
        accessorKey: 'olusturmaTarihi',
        header: 'Zaman',
        meta: { etiket: 'Zaman' },
        cell: ({ row }) => (
          <span className="text-fume-500 font-mono text-xs">
            {new Date(row.original.olusturmaTarihi).toLocaleString('tr-TR', {
              day: 'numeric',
              month: 'short',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
            })}
          </span>
        ),
      },
      {
        accessorKey: 'yapanAdSoyad',
        header: 'Yapan',
        meta: { etiket: 'Yapan' },
        cell: ({ row }) => <span className="text-xs">{row.original.yapanAdSoyad ?? '—'}</span>,
      },
      {
        accessorKey: 'eylem',
        header: 'Eylem',
        meta: { etiket: 'Eylem' },
        cell: ({ row }) => (
          <code className="text-marka-700 dark:text-marka-400 font-mono text-xs">
            {row.original.eylem}
          </code>
        ),
      },
      {
        accessorKey: 'kaynak',
        header: 'Kaynak',
        meta: { etiket: 'Kaynak' },
        cell: ({ row }) => (
          <code className="text-fume-600 dark:text-fume-300 font-mono text-xs">
            {row.original.kaynak}
          </code>
        ),
      },
      {
        id: 'fark',
        header: 'Değişiklik',
        meta: { etiket: 'Değişiklik' },
        enableSorting: false,
        cell: ({ row }) => {
          const oncesi = row.original.oncekiDeger;
          const sonrasi = row.original.yeniDeger;
          if (!oncesi && !sonrasi) return <span className="text-fume-400 text-xs">—</span>;
          return (
            <details className="text-xs">
              <summary className="text-marka-700 dark:text-marka-400 cursor-pointer">
                Göster
              </summary>
              <div className="mt-2 max-w-md space-y-1">
                {oncesi !== undefined && oncesi !== null && (
                  <div>
                    <span className="text-fume-400 text-[10px]">Önce:</span>
                    <pre className="bg-fume-100 dark:bg-fume-800 mt-0.5 overflow-x-auto rounded p-2 text-[11px]">
                      {JSON.stringify(oncesi, null, 2)}
                    </pre>
                  </div>
                )}
                {sonrasi !== undefined && sonrasi !== null && (
                  <div>
                    <span className="text-fume-400 text-[10px]">Sonra:</span>
                    <pre className="mt-0.5 overflow-x-auto rounded bg-emerald-50 p-2 text-[11px] dark:bg-emerald-950/30">
                      {JSON.stringify(sonrasi, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </details>
          );
        },
      },
      {
        accessorKey: 'ipAdresi',
        header: 'IP',
        meta: { etiket: 'IP' },
        cell: ({ row }) => (
          <span className="text-fume-400 font-mono text-[11px]">
            {row.original.ipAdresi ?? '—'}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Denetim Logları</h1>
        <p className="text-fume-500 dark:text-fume-400 mt-1 text-sm">
          Tüm admin yazma işlemlerinin (yaratma/güncelleme/silme) tutanağı.
        </p>
      </header>

      <VeriTablo
        veri={liste.data?.veriler ?? []}
        kolonlar={kolonlar}
        toplam={liste.data?.toplam}
        sayfa={sayfa}
        boyut={100}
        onSayfa={setSayfa}
        yukleniyor={liste.isLoading}
        yenileniyor={liste.isFetching}
        onYenile={() => void liste.refetch()}
        varsayilanGizliKolonlar={{ ipAdresi: false }}
        bosDurum={
          <BosDurum
            ikon={FileSearch}
            baslik="Bu kriterlerle kayıt yok"
            aciklama="Tarih veya filtreyi değiştirin."
          />
        }
        ustAksiyonlar={
          <>
            <TarihAraligiSecici
              deger={aralik}
              onDegisim={(a) => {
                setAralik(a);
                setSayfa(1);
              }}
              etiketYok
              className="h-10 text-xs"
            />
            <Filter size={13} className="text-fume-400" />
            <div className="relative">
              <Search
                size={13}
                className="text-fume-400 pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2"
              />
              <Girdi
                placeholder="eylem (kupon:olustur)"
                value={eylem}
                onChange={(e) => {
                  setEylem(e.target.value);
                  setSayfa(1);
                }}
                className="h-10 w-44 pl-8 font-mono text-xs"
              />
            </div>
            <div className="relative">
              <Search
                size={13}
                className="text-fume-400 pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2"
              />
              <Girdi
                placeholder="kaynak (kupon:abc)"
                value={kaynak}
                onChange={(e) => {
                  setKaynak(e.target.value);
                  setSayfa(1);
                }}
                className="h-10 w-44 pl-8 font-mono text-xs"
              />
            </div>
          </>
        }
      />
    </div>
  );
}
